import { AlertTriangle, CheckCircle2, Inbox, Info, Loader2, RotateCcw, SearchX, X } from 'lucide-react'

/**
 * Loading / empty / no-match / error. Audit: 74 loading wrappers, 51 empty
 * styles, 88 error styles — and five pages that showed an error AND "nothing
 * here" at the same time.
 *
 * The rule: these four are EXCLUSIVE. A page is in exactly one of
 *   loading (first load) · error · empty · no-match · content.
 * A REFRESH of a page that already has content is not "loading" — keep the
 * content on screen (DataTable dims it); never swap it for a spinner.
 */

/** variant 'block' (default — fills a card / section) | 'inline' (inside a sentence or toolbar) */
export function Loading({ label = 'กำลังโหลด…', variant = 'block' }) {
  if (variant === 'inline') {
    return (
      <span className="inline-flex items-center gap-1.5 text-[13px] text-slate-400" role="status">
        <Loader2 size={14} className="animate-spin" />{label}
      </span>
    )
  }
  return (
    <div className="flex items-center justify-center gap-2 py-14 text-[13.5px] text-slate-400" role="status">
      <Loader2 size={16} className="animate-spin" />{label}
    </div>
  )
}

/**
 * Nothing exists yet. `variant` 'card' (dashed box with icon — a whole section
 * is empty) | 'plain' (one line — inside a table or a small card).
 * `action` = the button that fixes it (e.g. "เพิ่มรายการแรก").
 */
export function EmptyState({ icon: Icon = Inbox, title = 'ยังไม่มีรายการ', hint, action, variant = 'card' }) {
  // 'compact' — one quiet line inside a small card (ยังไม่มีโน้ต); action on the same line.
  if (variant === 'compact') {
    return (
      <div className="flex items-center justify-between gap-3 py-2 text-[13px] text-slate-400">
        <span>{title}{hint && <span className="text-slate-300"> · {hint}</span>}</span>
        {action}
      </div>
    )
  }
  if (variant === 'plain') {
    return (
      <div className="py-10 text-center">
        <div className="text-[14px] text-slate-500">{title}</div>
        {hint && <div className="text-[13px] text-slate-400 mt-1">{hint}</div>}
        {action && <div className="mt-3">{action}</div>}
      </div>
    )
  }
  return (
    <div className="rounded-2xl border border-dashed border-slate-200 bg-white/60 px-6 py-12 text-center">
      <div className="w-11 h-11 rounded-xl bg-slate-100 text-slate-400 flex items-center justify-center mx-auto mb-3">
        <Icon size={20} strokeWidth={1.75} />
      </div>
      <div className="text-[14.5px] font-medium text-slate-700">{title}</div>
      {hint && <div className="text-[13.5px] text-slate-400 mt-1">{hint}</div>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  )
}

/** Data exists, the filters hide all of it. Always offers the way out. */
export function NoMatchState({ onClear, title = 'ไม่พบรายการที่ตรงกับตัวกรอง', variant = 'plain' }) {
  return (
    <EmptyState
      icon={SearchX}
      variant={variant}
      title={title}
      hint="ลองเปลี่ยนคำค้นหรือตัวกรอง"
      action={onClear && (
        <button type="button" onClick={onClear}
          className="inline-flex items-center gap-1.5 text-[13px] font-medium text-brand-700 hover:text-brand-800">
          <RotateCcw size={13} />ล้างตัวกรองทั้งหมด
        </button>
      )}
    />
  )
}

/** A load failed. Never looks like "empty". `onRetry` adds ลองอีกครั้ง. */
export function ErrorState({ title = 'โหลดข้อมูลไม่สำเร็จ', message, onRetry, className = '' }) {
  return (
    <div className={`rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-rose-700 flex items-start gap-2.5 ${className}`} role="alert">
      <AlertTriangle size={16} className="flex-none mt-0.5" />
      <div className="flex-1 min-w-0">
        <div className="text-[14px] font-medium">{title}</div>
        {message && <div className="text-[13px] text-rose-600 mt-0.5 break-words">{message}</div>}
      </div>
      {onRetry && (
        <button type="button" onClick={onRetry}
          className="flex-none inline-flex items-center gap-1 text-[13px] font-medium text-rose-700 hover:text-rose-900 px-2 py-1 rounded-lg hover:bg-rose-100">
          <RotateCcw size={13} />ลองอีกครั้ง
        </button>
      )}
    </div>
  )
}

/**
 * <Banner> — a message that belongs to the PAGE or the RECORD and stays until it no
 * longer applies: "แก้ไขจากเครื่องอื่น · โหลดใหม่", "วันนี้ปิดยอดแล้ว", "แสดงเฉพาะ 500
 * รายการแรก". Not for save results (that is a toast) and not for load failures
 * (that is <ErrorState>).
 *   tone     'info' | 'warn' | 'error' | 'success'
 *   title    bold first line (optional) · children = the text
 *   action   a button / link on the right (e.g. โหลดใหม่)
 *   onClose  shows ✕ — only when dismissing is genuinely safe
 */
const BANNER = {
  info: { cls: 'bg-sky-50 border-sky-200 text-sky-800', Icon: Info },
  warn: { cls: 'bg-amber-50 border-amber-200 text-amber-800', Icon: AlertTriangle },
  error: { cls: 'bg-rose-50 border-rose-200 text-rose-700', Icon: AlertTriangle },
  success: { cls: 'bg-emerald-50 border-emerald-200 text-emerald-800', Icon: CheckCircle2 },
}
export function Banner({ tone = 'info', title, action, onClose, icon, className = '', children }) {
  const t = BANNER[tone] || BANNER.info
  const Icon = icon || t.Icon
  return (
    <div className={`rounded-xl border px-3.5 py-2.5 flex items-start gap-2.5 text-[13.5px] ${t.cls} ${className}`} role={tone === 'error' ? 'alert' : 'status'}>
      <Icon size={15} className="flex-none mt-0.5" />
      <div className="flex-1 min-w-0">
        {title && <div className="font-semibold">{title}</div>}
        {children && <div className={title ? 'mt-0.5 opacity-90' : ''}>{children}</div>}
      </div>
      {action && <div className="flex-none">{action}</div>}
      {onClose && (
        <button type="button" onClick={onClose} aria-label="ปิด" className="flex-none -mr-1 w-6 h-6 rounded-md flex items-center justify-center opacity-60 hover:opacity-100 hover:bg-black/5">
          <X size={14} />
        </button>
      )}
    </div>
  )
}

/** Inline form/action error — one line under a form or inside a drawer footer. */
export function InlineError({ children }) {
  if (!children) return null
  return <p className="text-[12.5px] text-rose-600 flex items-center gap-1"><AlertTriangle size={13} className="flex-none" />{children}</p>
}
